import { AxiosError } from 'axios';
import { JobStatusEnum, ProfessionTypeEnum } from '~/models';
import { IOnsiteInterview } from '~/reducers/permanentJobsReducer';
import { createTestDate } from '~/utils/testUtils/date';
import {
  CandidateStatus,
  HiringStatus,
  JobPostingStatus,
  OnsiteInterviewSortableField,
  OnsiteInterviewStatus,
  PayType,
  ProfessionalStatus,
  ScheduleType,
} from '../enums';
import {
  ICandidateListModel,
  ICandidateListViewEntity,
  ICreateOnsiteInterviewPayload,
  ICreateShortlistCandidatePayload,
  IDeleteShortlistCandidatePayload,
  IDraftJobPostEntity,
  IDraftJobPostListViewEntity,
  IDraftJobPostModel,
  IGetJobPostingsResult,
  IGetOnsiteInterviewListPayload,
  IJobPostDetailEntity,
} from '../types';
import {
  ICandidateProfessionalApiModel,
  IGetCandidateProfessionalApiResponse,
} from '../types/CandidateProfessional.api';
import {
  IGetOnsiteInterviewListApiResponse,
  IGetOnsiteInterviewStatusCountApiResponse,
  IOnsiteInterviewListMetadata,
  IOnsiteInterviewModel,
} from '../types/OnsiteInterview.api';

export const createMockIDraftJobPostModel = (
  overrides?: Partial<IDraftJobPostModel>,
): IDraftJobPostModel =>
  ({
    id: 'job-posting-id-mock',
    officeId: 'office-id-mock',
    officeName: 'Example Office',
    officeSelected: true,
    title: 'Example Job Posting',
    benefits: ['Health Insurance'],
    benefitsOther: '',
    createdAt: '2024-01-01T00:00:00Z',
    createdBy: 'Example User',
    description: '<p>lorem ipsum dolor sil amet.</p>',
    expectedDays: [1, 2, 3, 4, 5],
    professionType: 'RDH',
    scheduleType: 'Full Time',
    specialty: 'General Dentistry',
    status: 'draft',
    updatedAt: '2024-01-02T00:00:00Z',
    updatedBy: 'Example User',
    ...overrides,
    salaryRange: {
      max: 45,
      min: 35,
      payType: 'Hourly',
      ...overrides?.salaryRange,
    },
  }) satisfies IDraftJobPostModel;

export const createMockIDraftJobPostModelLite = (
  overrides?: Partial<IDraftJobPostModel>,
): IDraftJobPostModel =>
  ({
    id: 'job-posting-id-mock',
    officeId: 'office-id-mock',
    officeName: 'Example Office',
    officeSelected: true,
    status: 'draft',
    title: 'Example Job Posting',
    ...overrides,
  }) satisfies IDraftJobPostModel;

export const createMockIDraftJobPostEntity = (
  overrides?: Partial<IDraftJobPostEntity>,
): IDraftJobPostEntity =>
  ({
    benefits: ['Health Insurance'],
    benefitsOther: '',
    closedAt: null,
    closedBy: null,
    createdAt: '2024-01-01T00:00:00Z',
    createdBy: 'Example User',
    description: '<p>lorem ipsum dolor sil amet.</p>',
    expectedDays: [1, 2, 3, 4, 5],
    id: 'job-posting-id-mock',
    maxRate: 45,
    minRate: 35,
    officeId: 'office-id-mock',
    officeName: 'Example Office',
    officeSelected: true,
    officeSpecialty: 'specialty_General Dentistry',
    payType: PayType.HOURLY,
    professionType: ProfessionTypeEnum.RDH,
    publishedAt: null,
    scheduleType: ScheduleType.FULL_TIME,
    status: JobPostingStatus.Draft,
    subProfession: null,
    title: 'Example Job Posting',
    updatedAt: '2024-01-02T00:00:00Z',
    updatedBy: 'Example User',
    yearlyRate: null,
    ...overrides,
  }) satisfies IDraftJobPostEntity;

export const createMockIDraftJobPostEntityLite = (
  overrides?: Partial<IDraftJobPostEntity>,
): IDraftJobPostEntity =>
  ({
    benefits: null,
    benefitsOther: null,
    closedAt: null,
    closedBy: null,
    createdAt: null,
    createdBy: null,
    description: null,
    expectedDays: undefined,
    id: 'job-posting-id-mock',
    maxRate: null,
    minRate: null,
    officeId: 'office-id-mock',
    officeName: 'Example Office',
    officeSpecialty: null,
    officeSelected: true,
    payType: null,
    professionType: null,
    publishedAt: null,
    scheduleType: null,
    status: JobPostingStatus.Draft,
    subProfession: null,
    title: 'Example Job Posting',
    updatedAt: null,
    updatedBy: null,
    yearlyRate: null,
    ...overrides,
  }) satisfies IDraftJobPostEntity;

export const createMockIJobPostDetailEntity = (
  overrides?: Partial<IJobPostDetailEntity>,
): IJobPostDetailEntity =>
  ({
    benefits: ['Health Insurance', '401k'],
    benefitsOther: null,
    closedAt: null,
    closedBy: null,
    createdAt: '2024-01-01T00:00:00Z',
    createdBy: 'Example User',
    description: '<p>lorem ipsum dolor sil amet.</p>',
    expectedDays: [1, 2, 3, 4, 5],
    id: 'job-posting-id-mock',
    maxRate: 45,
    minRate: 35,
    officeId: 'office-id-mock',
    officeName: 'Example Office',
    officeSelected: true,
    officeSpecialty: 'specialty_General Dentistry',
    payType: PayType.HOURLY,
    professionType: ProfessionTypeEnum.RDH,
    publishedAt: '2024-01-02T00:00:00Z',
    scheduleType: ScheduleType.FULL_TIME,
    status: JobPostingStatus.Active,
    subProfession: null,
    title: 'Example Job Posting',
    updatedAt: '2024-01-03T00:00:00Z',
    updatedBy: 'Example User',
    yearlyRate: null,
    planId: null,
    officePlanId: null,
    candidateCount: null,
    officeState: null,
    planName: null,
    suspendedAt: null,
    ...overrides,
  }) satisfies IJobPostDetailEntity;

export const createMockICandidateListModel = (
  overrides?: Partial<ICandidateListModel>,
): ICandidateListModel =>
  ({
    id: 'candidate-001',
    professionalId: 'prof-001',
    professionalName: 'Angelique Rosenberg',
    professionalPicture: 'https://example.com/avatar.jpg',
    officeStatus: CandidateStatus.Applied,
    yearsOfExperience: 7,
    isShortlisted: false,
    onsiteInterviewId: 'interview-001',
    createdAt: '2025-07-14T09:00:00Z',
    createdBy: 'user-001',
    updatedAt: '2025-07-14T09:00:00Z',
    updatedBy: 'user-001',
    ...overrides,
  }) satisfies ICandidateListModel;

export const createMockICandidateListViewEntity = (
  overrides?: Partial<ICandidateListViewEntity>,
): ICandidateListViewEntity =>
  ({
    id: 'candidate-001',
    professional: {
      id: 'prof-001',
      name: 'Angelique Rosenberg',
      picture: 'https://example.com/avatar.jpg',
      ...overrides?.professional,
    },
    officeStatus: CandidateStatus.Applied,
    yearsOfExperience: 7,
    isShortlisted: false,
    onsiteInterviewId: 'interview-001',
    createdAt: '2025-07-14T09:00:00Z',
    updatedAt: '2025-07-14T09:00:00Z',
    ...overrides,
  }) satisfies ICandidateListViewEntity;

export const createMockAxiosError = (
  message: string,
  response?: any,
  code?: string,
): AxiosError => {
  const error = new Error(message) as AxiosError;
  error.name = 'AxiosError';
  error.isAxiosError = true;
  error.toJSON = () => ({});
  error.config = {} as any;
  if (response) {
    error.response = response;
  }
  if (code) {
    error.code = code;
  }
  return error;
};

export const createMockOnsiteInterviewReduxState = (
  overrides?: Partial<IOnsiteInterview>,
): IOnsiteInterview => ({
  id: '1',
  breakTime: '30 min',
  date: createTestDate(2024, 1, 15),
  hourlyRate: '45.50',
  hoursArrival: '09:00 am',
  hoursDeparture: '05:00 pm',
  isAssistedHygiene: false,
  isPaidBreakTime: true,
  profession: 'RDH',
  specialty: 'General Dentistry',
  ...overrides,
});

export const createMockCreateOnsiteInterviewPayload = (
  overrides?: Partial<ICreateOnsiteInterviewPayload>,
): ICreateOnsiteInterviewPayload => ({
  jobPostingId: 'job-123',
  onsiteInterviews: [createMockOnsiteInterviewReduxState()],
  duplicate: false,
  ...overrides,
});

export const createMockGetOnsiteInterviewListPayload = (
  overrides?: Partial<IGetOnsiteInterviewListPayload>,
): IGetOnsiteInterviewListPayload => ({
  jobPostingId: 'job-posting-123',
  status: OnsiteInterviewStatus.Open,
  orderBy: OnsiteInterviewSortableField.JobDateTime,
  orderDirection: 'asc',
  page: 1,
  limit: 10,
  ...overrides,
});

export const createMockGetOnsiteInterviewStatusCountPayload = (
  overrides?: Partial<{ jobPostingId: string }>,
): { jobPostingId: string } => ({
  jobPostingId: 'job-posting-123',
  ...overrides,
});

export const createMockIOnsiteInterviewModel = (
  overrides?: Partial<IOnsiteInterviewModel>,
): IOnsiteInterviewModel => ({
  id: 'onsite-interview-001',
  jobStatus: JobStatusEnum.Open,
  isLunchBreakPaid: true,
  jobDatetime: '2024-01-15T09:00:00Z',
  jobDatetimeEnd: '2024-01-15T17:00:00Z',
  lunchFinalBreakTime: '30 min',
  rate: 45.5,
  profession: 'RDH',
  professionName: 'Registered Dental Hygienist',
  subProfession: null,
  subProfessionName: null,
  timezone: 'America/Los_Angeles',
  specialty: 'General Dentistry',
  procedures: [],
  createdAt: '2024-01-10T10:00:00Z',
  state: 'CA',
  total: 364,
  totalHours: 8,
  workingHours: 7.5,
  localStartTime: '09:00',
  localEndTime: '17:00',
  localDate: '2024-01-15',
  localTimezone: 'America/Los_Angeles',
  multiDayBooking: 'no',
  insuranceAmount: 0,
  isFavorite: false,
  parkingInfo: 'Street parking available',
  parkingType: 'street',
  workerClassification: 'W2',
  timePerPatient: '30 minutes',
  isAssistedHygiene: false,
  isUrgent: false,
  scheduled: false,
  candidatesCount: 5,
  professional: null,
  ...overrides,
});

export const createMockIOnsiteInterviewListMetadata = (
  overrides?: Partial<IOnsiteInterviewListMetadata>,
): IOnsiteInterviewListMetadata => ({
  pagination: {
    page: 1,
    pageSize: 10,
    total: 25,
  },
  postingInfo: {
    postingId: 'job-posting-123',
  },
  offerOwner: {
    id: 'user-001',
    email: 'user@example.com',
    firstName: 'John',
    lastName: 'Doe',
    fullName: 'John Doe',
  } as any,
  ...overrides,
});

export const createMockGetOnsiteInterviewListApiResponse = (
  overrides?: Partial<IGetOnsiteInterviewListApiResponse>,
): IGetOnsiteInterviewListApiResponse => ({
  data: [createMockIOnsiteInterviewModel()],
  metadata: createMockIOnsiteInterviewListMetadata(),
  ...overrides,
});

export const createMockGetOnsiteInterviewStatusCountApiResponse = (
  overrides?: Partial<IGetOnsiteInterviewStatusCountApiResponse>,
): IGetOnsiteInterviewStatusCountApiResponse => ({
  data: {
    actionNeeded: 5,
    active: 10,
    cancelledExpired: 2,
    completed: 15,
    open: 8,
    scheduled: 12,
  },
  ...overrides,
});

export const createMockCreateShortlistCandidatePayload = (
  overrides?: Partial<ICreateShortlistCandidatePayload>,
): ICreateShortlistCandidatePayload => ({
  candidateId: 'candidate-123',
  ...overrides,
});

export const createMockDeleteShortlistCandidatePayload = (
  overrides?: Partial<IDeleteShortlistCandidatePayload>,
): IDeleteShortlistCandidatePayload => ({
  candidateId: 'candidate-123',
  ...overrides,
});

export const createMockDeleteDraftJobPayload = (
  overrides?: Partial<import('../types').IDeleteDraftJobPayload>,
): import('../types').IDeleteDraftJobPayload => ({
  jobPostingId: 'job-posting-123',
  ...overrides,
});

export const createMockIDraftJobPostListViewEntity = (
  overrides?: Partial<IDraftJobPostListViewEntity>,
): IDraftJobPostListViewEntity => ({
  id: 'job-posting-123',
  lastSavedAt: '2024-01-15T10:00:00Z',
  office: {
    id: 'office-123',
    name: 'Example Dental Office',
  },
  officeSelected: true,
  professionType: ProfessionTypeEnum.RDH,
  status: JobPostingStatus.Draft,
  subProfession: null,
  title: 'Dental Hygienist Position',
  ...overrides,
});

export const createMockIGetJobPostingsResult = (
  overrides?: Partial<IGetJobPostingsResult>,
): IGetJobPostingsResult => ({
  data: [createMockIDraftJobPostListViewEntity()],
  pagination: {
    page: 1,
    pageSize: 10,
    total: 1,
    totalPages: 1,
  },
  statistics: {
    tabs: {
      [JobPostingStatus.Active]: 0,
      [JobPostingStatus.Closed]: 0,
      [JobPostingStatus.Draft]: 1,
      [JobPostingStatus.Suspended]: 0,
    },
  },
  ...overrides,
});

export const createMockICandidateProfessionalApiModel = (
  overrides?: Partial<ICandidateProfessionalApiModel>,
): ICandidateProfessionalApiModel => ({
  createdAt: '2024-01-01T00:00:00Z',
  createdBy: 'user-001',
  deletedAt: null,
  deletedBy: null,
  hiringStatus: HiringStatus.UnderReview,
  id: 'candidate-prof-001',
  isDeleted: false,
  isShortlisted: false,
  officeStatus: CandidateStatus.Applied,
  postingDetails: {
    benefits: ['Health Insurance', '401k'],
    candidateCount: 5,
    expectedDays: [1, 2, 3, 4, 5],
    id: 'posting-001',
    maxRate: 50,
    minRate: 40,
    officeId: 'office-001',
    officeName: 'Example Dental Office',
    officeSpecialty: 'General Dentistry',
    payType: PayType.HOURLY,
    postingDescription: '<p>We are looking for a dental hygienist</p>',
    postingTitle: 'Dental Hygienist Position',
    procedures: ['Cleaning', 'X-rays'],
    professionType: ProfessionTypeEnum.RDH,
    recruiterEmail: 'recruiter@example.com',
    scheduleType: ScheduleType.FULL_TIME,
    state: 'CA',
    status: JobPostingStatus.Active,
    subProfession: null,
    timezone: 'America/Los_Angeles',
  },
  postingId: 'posting-001',
  professionalEmail: 'professional@example.com',
  professionalId: 'professional-001',
  professionalName: 'Jane Smith',
  professionalPhone: '+1234567890',
  professionalStatus: ProfessionalStatus.Applied,
  updatedAt: '2024-01-02T00:00:00Z',
  updatedBy: 'user-001',
  ...overrides,
});

export const createMockIGetCandidateProfessionalApiResponse = (
  overrides?: Partial<IGetCandidateProfessionalApiResponse>,
): IGetCandidateProfessionalApiResponse => ({
  data: createMockICandidateProfessionalApiModel(overrides?.data),
  ...overrides,
});

export const createMockGetCandidateProfessionalPayload = (
  overrides?: Partial<import('../types').IGetCandidateProfessionalPayload>,
): import('../types').IGetCandidateProfessionalPayload => ({
  postingId: 'posting-001',
  professionalId: 'professional-001',
  ...overrides,
});

export const createMockICandidateProfessionalEntity = (
  overrides?: Partial<import('../types').ICandidateProfessionalEntity>,
): import('../types').ICandidateProfessionalEntity => ({
  id: 'candidate-prof-001',
  isShortlisted: false,
  officeStatus: CandidateStatus.Applied,
  ...overrides,
});
